import type React from 'react';

import { Checkbox } from '@vlabs/uikit/components/controls/checkbox';
import type { WithDataTestid } from '@vlabs/uikit/types';
import type { PathValue } from 'react-hook-form';

import type { EditPermissionsFormProps, FilterPropsByPermission } from '../types';
import { useEditPermissionsFormContext } from '../useEditPermissionsFormContext';
import st from './Cell.module.sass';

type Permission = 'read' | 'create' | 'update' | 'delete';

interface AllCellProps<T extends Permission> extends WithDataTestid {
  fields: (keyof FilterPropsByPermission<T>)[];
  permission: T;
  disabled?: boolean;
}

const forceCheck = (arr: string[], val: string): string[] => (
  arr.includes(val)
    ? arr
    : [...arr, val]
);

const forceUncheck = (arr: string[], val: string): string[] => (
  arr.includes(val)
    ? arr.filter((el) => el !== val)
    : arr
);

function AllCell<T extends Permission>({
  fields,
  permission,
  disabled = false,
  'data-testid': dataTestId = undefined,
}: AllCellProps<T>): React.JSX.Element {
  const { watch, setValue } = useEditPermissionsFormContext();
  const permissionList = watch(fields) ?? [];
  const checkedCount = Object.values(permissionList).map(
    // @ts-expect-error typescript уже слишком заигрывается
    (fieldPermissions) => Number((fieldPermissions ?? []).includes(permission)),
  ).reduce((summ, isIncluded) => summ + isIncluded, 0);
  const indeterminate = checkedCount !== 0 && checkedCount !== fields.length;
  const checked = checkedCount === fields.length;

  return (
    <div className={st.Cell}>
      <Checkbox
        checked={checked || indeterminate}
        data-testid={dataTestId}
        disabled={disabled}
        indeterminate={indeterminate}
        onClick={() => {
          const method = checked ? forceUncheck : forceCheck;
          fields.forEach((field, idx) => {
            setValue(
              field,
              method(
                (permissionList[idx] ?? []) as string[],
                permission,
              ) as PathValue<EditPermissionsFormProps, typeof field>,
            );
          });
        }}
      />
    </div>
  );
}

export { AllCell };
